'use client';

import { useScroll, useTransform, type MotionValue } from 'motion/react';
import { MAIN_XL_MAX_QUERY } from '@/constants/main';
import { getVisibleHeaderBottom } from '@/components/header/get-visible-header-bottom';
import { cn } from '@lib/utils';
import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';

interface Props {
  heightVh: number;
  startHoldPx?: number;
  endHoldPx?: number;
  // 고정 장면이 필요로 하는 최소 높이. 넘기지 않으면 지금 그려진 내용의 높이로 판단하는데,
  // 그 내용이 고정이 풀렸을 때 나오는 폴백이면 폴백 높이가 기준이 돼 고정이 영영 안 걸린다.
  minContentPx?: number;
  nonStickyEnd?: 'end start' | 'end end';
  // 모바일에서 헤더 아래가 아니라 화면 최상단(헤더 뒤)에 고정한다. 반투명 헤더 뒤로 장면이 비치고
  // 콘텐츠가 화면 전체 높이의 가운데에 온다.
  mobileUnderHeader?: boolean;
  // 콘텐츠가 화면보다 이 값 이하로 크면 고정을 풀지 않고, 콘텐츠 아래 끝을 화면 아래에 맞춰 고정한다
  // (윗부분은 화면 위로 넘어간 채). 넘치는 윗부분에 스크롤로 바뀌는 요소가 없을 때만 쓴다.
  maxOverflowPx?: number;
  children: (
    progress: MotionValue<number>,
    isSticky: boolean,
    scrollToProgress: (value: number) => void,
  ) => ReactNode;
  className?: string;
}

export default function ScrollStage({
  heightVh,
  startHoldPx = 0,
  endHoldPx = 0,
  minContentPx,
  nonStickyEnd = 'end start',
  mobileUnderHeader = false,
  maxOverflowPx = 0,
  children,
  className,
}: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [layout, setLayout] = useState({ top: 0, height: 0, sticky: false });

  useEffect(() => {
    const content = contentRef.current;
    if (!content) return;
    const header = document.querySelector('header');
    // 모바일 브라우저는 스크롤 중 주소창이 접히고 펼쳐질 때마다 화면 높이를 바꾼다.
    // 그때마다 다시 재면 직전 높이로 그려진 콘텐츠가 새 높이보다 커져 고정이 한 프레임 풀리고,
    // 그 사이 고정 구간 높이가 사라져 화면이 크게 튄다. 그래서 가장 큰 높이를 유지한다.
    // 유지하는 건 화면 높이뿐이다 — 헤더 높이는 배너가 붙으면 실제로 달라지므로 그대로 반영한다.
    const holdsHeight = window.matchMedia('(pointer: coarse)').matches;
    let heldWidth = 0;
    let heldViewport = 0;
    // 메가메뉴는 헤더 안에서 펼쳐져 헤더 높이를 키운다. 그대로 재면 hover 만으로 고정 구간이 풀려
    // 페이지 높이가 무너지고 화면이 튀므로 펼침 높이는 뺀다.
    const measure = () => {
      const megaMenu = header?.querySelector<HTMLElement>('[data-mega-menu]');
      const underHeader =
        mobileUnderHeader && window.matchMedia(MAIN_XL_MAX_QUERY).matches;
      let top = underHeader
        ? 0
        : getVisibleHeaderBottom() - (megaMenu?.offsetHeight ?? 0);
      // 화면 회전으로 가로 폭이 바뀌면 유지하던 높이를 버린다.
      if (window.innerWidth !== heldWidth) {
        heldWidth = window.innerWidth;
        heldViewport = 0;
      }
      heldViewport = Math.max(heldViewport, window.innerHeight);
      const viewport = holdsHeight ? heldViewport : window.innerHeight;
      let height = Math.max(0, viewport - top);
      let sticky = (minContentPx ?? content.scrollHeight) <= height + 1;
      if (!sticky && maxOverflowPx > 0) {
        // 고정 높이(min-h)를 뺀 원래 높이로 잰다. 직전 고정 높이가 섞이면 넘침이 부풀어 아래가 빈다.
        const previousMinHeight = content.style.minHeight;
        content.style.minHeight = '0px';
        const natural = minContentPx ?? content.scrollHeight;
        content.style.minHeight = previousMinHeight;
        if (natural - height <= maxOverflowPx) {
          top = viewport - natural;
          height = natural;
          sticky = true;
        }
      }
      setLayout(previous =>
        previous.top === top &&
        previous.height === height &&
        previous.sticky === sticky
          ? previous
          : { top, height, sticky },
      );
    };
    const observer = new ResizeObserver(measure);
    observer.observe(content);
    if (header) observer.observe(header);
    window.addEventListener('resize', measure);
    measure();
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [minContentPx, mobileUnderHeader, maxOverflowPx]);

  // 높이가 부족하면 고정을 풀고 일반 스크롤의 통과 구간을 사용한다.
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: layout.sticky
      ? [`start ${layout.top}px`, 'end end']
      : ['start end', nonStickyEnd],
  });

  const animationDistance =
    ((layout.height + layout.top) * heightVh) / 100 - layout.height;
  const totalDistance = startHoldPx + animationDistance + endHoldPx;
  const hasAnimation = layout.sticky && animationDistance > 0;
  const animationStart = hasAnimation ? startHoldPx / totalDistance : 0;
  const animationEnd = hasAnimation
    ? (startHoldPx + animationDistance) / totalDistance
    : 1;
  const progress = useTransform(
    scrollYProgress,
    [animationStart, animationEnd],
    [0, 1],
  );

  // 진행률(0~1)에 해당하는 문서 위치로 즉시 이동한다. 고정 구간 계산은 여기서만 한다.
  const scrollToProgress = (value: number) => {
    const element = ref.current;
    if (!element || !hasAnimation) return;
    const start =
      element.getBoundingClientRect().top + window.scrollY - layout.top;
    window.scrollTo({
      top: start + startHoldPx + animationDistance * value,
      behavior: 'instant',
    });
  };

  return (
    <div
      ref={ref}
      // 실행 중 정해지는 값만 CSS 변수로 전달하고 스타일은 Tailwind로 정의한다.
      style={
        {
          // 측정 전(SSR)에는 vh 로, 측정 후에는 진행률 계산과 같은 px 로 높이를 맞춘다.
          '--scroll-stage-distance': layout.height
            ? `${layout.height + totalDistance}px`
            : `calc(${heightVh}vh + ${startHoldPx + endHoldPx}px)`,
          '--scroll-stage-top': `${layout.top}px`,
          '--scroll-stage-height': layout.height
            ? `${layout.height}px`
            : '100svh',
        } as CSSProperties
      }
      className={cn(
        'group/scroll-stage data-[sticky=true]:h-[var(--scroll-stage-distance)]',
        className,
      )}
      data-scroll-stage
      data-sticky={layout.sticky}
    >
      <div className="relative group-data-[sticky=true]/scroll-stage:sticky group-data-[sticky=true]/scroll-stage:top-[var(--scroll-stage-top)]">
        <div ref={contentRef} className="min-h-[var(--scroll-stage-height)]">
          {children(progress, layout.sticky, scrollToProgress)}
        </div>
      </div>
    </div>
  );
}
